import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { X, Calendar, Loader2, AlertTriangle, CalendarPlus } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import {
  CLINIC_TIME_ZONE,
  addClinicDays,
  clinicDateAt,
  clinicDateStringFromInstant,
} from "@/lib/clinicTime";

// =====================================================================
// SchedulePlanItemDialog — pick an upcoming appointment and link a
// plan item to it. Calls public.schedule_plan_item(item_id, appt_id).
//
// V1 lists appointments via a direct PostgREST select (RLS keeps it
// scoped to the active clinic); patient-side appointment creation
// lives in the future Appointments tab.
// =====================================================================

const ELIGIBLE_STATUSES = ["Scheduled", "Confirmed", "Checked In"];

function formatRange(start, end) {
  if (!start) return "—";
  try {
    const s = new Date(start);
    const e = end ? new Date(end) : null;
    const date = s.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", timeZone: CLINIC_TIME_ZONE });
    const t1 = s.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", timeZone: CLINIC_TIME_ZONE });
    const t2 = e ? e.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", timeZone: CLINIC_TIME_ZONE }) : null;
    return t2 ? `${date}, ${t1}–${t2}` : `${date}, ${t1}`;
  } catch { return start; }
}

// Accepts EITHER:
//   item  — single plan-item (the per-row Schedule action)
//   items — array of plan-items (the "Schedule whole phase" action)
//   title — optional override shown under the dialog heading
//   plan  — full plan (used to compute the suggested booking window
//           when the targeted phase has timing + the reference phase
//           has at least one scheduled appointment)
//   phaseNumber — which phase number is being scheduled (set when the
//                 caller is the "Schedule whole phase" action)
export default function SchedulePlanItemDialog({
  open, onOpenChange, item, items, title, patientId, plan, phaseNumber, onScheduled,
}) {
  const navigate = useNavigate();
  const targets = items && items.length ? items : (item ? [item] : []);
  const [appts, setAppts]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // We need both upcoming + recent past appointments. Past ones don't
  // show in the picker but are needed to look up the reference phase's
  // anchor date when computing the suggested window.
  const [refAppt, setRefAppt] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    const nowIso = new Date().toISOString();
    Promise.all([
      // Upcoming, eligible appointments — picker list.
      supabase
        .from("appointments")
        .select("id, scheduled_start, scheduled_end, status, doctor_id, notes")
        .eq("patient_id", patientId)
        .in("status", ELIGIBLE_STATUSES)
        .gte("scheduled_start", nowIso)
        .order("scheduled_start", { ascending: true })
        .limit(50),
      // Anchor lookup: any appointment linked to an item in the
      // reference phase. Span past + future, any status.
      (async () => {
        const meta = phaseNumber ? plan?.phase_meta?.[String(phaseNumber)] : null;
        if (!meta?.offset_days || !meta.after_phase) return { data: null };
        const refItems = (plan?.items ?? []).filter(
          (i) => Number(i.phase_number) === Number(meta.after_phase)
            && i.scheduled_appointment_id,
        );
        if (refItems.length === 0) return { data: null };
        const apptIds = [...new Set(refItems.map((i) => i.scheduled_appointment_id))];
        return supabase
          .from("appointments")
          .select("id, scheduled_start")
          .in("id", apptIds)
          .order("scheduled_start", { ascending: true })
          .limit(1)
          .single();
      })(),
    ]).then(([listRes, refRes]) => {
      setLoading(false);
      if (listRes.error) { setError(listRes.error.message ?? "Could not load appointments."); return; }
      setAppts(listRes.data ?? []);
      setRefAppt(refRes?.data || null);
    });
  }, [open, patientId, plan, phaseNumber]);

  // Compute the suggested booking window from the active phase's meta
  // + the reference phase's anchor appointment.
  const window = (() => {
    const meta = phaseNumber ? plan?.phase_meta?.[String(phaseNumber)] : null;
    if (!meta?.offset_days || !refAppt?.scheduled_start) return null;
    const anchorDate = clinicDateStringFromInstant(refAppt.scheduled_start);
    if (!anchorDate) return null;
    const targetDate = addClinicDays(anchorDate, meta.offset_days);
    const target = clinicDateAt(targetDate, 12);
    // Soft window: ±3 days around the target date.
    const start = clinicDateAt(addClinicDays(targetDate, -3), 0);
    const end   = clinicDateAt(addClinicDays(targetDate, 3), 23, 59);
    const refLabel = plan?.phase_names?.[String(meta.after_phase)] || `Phase ${meta.after_phase}`;
    return { start, end, target, refLabel };
  })();
  function fmtDate(d) {
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: CLINIC_TIME_ZONE });
  }
  function alignmentForApt(apptStart) {
    if (!window) return null;
    const t = new Date(apptStart).getTime();
    if (t < window.start.getTime()) {
      const days = Math.round((window.target.getTime() - t) / 86400000);
      return { kind: "early", label: `${days} day${days === 1 ? "" : "s"} early` };
    }
    if (t > window.end.getTime()) {
      const days = Math.round((t - window.target.getTime()) / 86400000);
      return { kind: "late", label: `${days} day${days === 1 ? "" : "s"} late` };
    }
    return { kind: "in", label: "in window" };
  }

  const close = () => { if (!submitting) onOpenChange(false); };

  async function pick(apptId) {
    if (submitting || targets.length === 0) return;
    setSubmitting(true);
    let okCount = 0;
    let firstErr = null;
    const succeeded = [];
    for (const t of targets) {
      const { error: err } = await supabase.rpc("schedule_plan_item", {
        p_item_id: t.id, p_appointment_id: apptId,
      });
      if (err) { firstErr = err; break; }
      okCount += 1;
      succeeded.push(t);
    }
    setSubmitting(false);
    if (firstErr) {
      toast.error(`${firstErr.message ?? "Could not schedule."}${okCount ? ` (${okCount} succeeded)` : ""}`);
      // Still refresh so the partial result is reflected. Don't record
      // an undo entry on partial failures — clean half-state is messy.
      onScheduled?.({ apptId, items: succeeded, partial: true });
      return;
    }
    toast.success(targets.length === 1
      ? "Item scheduled to appointment."
      : `${targets.length} items scheduled to appointment.`);
    onScheduled?.({ apptId, items: succeeded, partial: false });
    onOpenChange(false);
  }

  if (!open) return null;

  return (
    <div
      role="dialog" aria-modal="true"
      data-testid="schedule-plan-item-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-slate-900">Schedule onto appointment</h2>
            <p className="text-xs text-slate-500 mt-0.5 truncate">
              {title
                ? title
                : targets.length === 1
                  ? `${targets[0]?.procedure_name}${targets[0]?.surface ? ` (${targets[0].surface})` : ""}`
                  : `${targets.length} items`}
            </p>
          </div>
          <button
            type="button"
            onClick={close}
            disabled={submitting}
            data-testid="schedule-plan-item-close"
            className="p-1 rounded hover:bg-slate-100 text-slate-500 disabled:opacity-40"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
          {window ? (
            <div className="mb-3 px-3 py-2 rounded-lg bg-teal-50 border border-teal-200 text-xs text-teal-900 flex items-start gap-2"
                 data-testid="schedule-plan-item-window">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>
                Suggested window: <strong>{fmtDate(window.start)} – {fmtDate(window.end)}</strong>
                {" "}(target: {fmtDate(window.target)}, {window.refLabel} anchored {fmtDate(new Date(refAppt.scheduled_start))})
              </span>
            </div>
          ) : null}
          {loading ? (
            <div className="text-sm text-slate-500 py-8 text-center">
              <Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Loading upcoming appointments…
            </div>
          ) : error ? (
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-sm text-rose-700">{error}</div>
          ) : appts.length === 0 ? (
            <div className="text-center py-8" data-testid="schedule-plan-item-empty">
              <div className="w-12 h-12 rounded-full bg-slate-100 mx-auto flex items-center justify-center">
                <Calendar className="w-5 h-5 text-slate-400" />
              </div>
              <p className="mt-3 text-sm font-semibold text-slate-900">No upcoming appointments</p>
              <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
                Create an appointment for this patient first, then come back to schedule this item.
              </p>
              <button
                type="button"
                onClick={() => {
                  onOpenChange?.(false);
                  navigate(`/calendar?patient=${patientId || ""}`);
                }}
                className="mt-4 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-md text-[13px] font-medium bg-teal-600 text-white hover:bg-teal-700"
              >
                <CalendarPlus className="w-4 h-4" strokeWidth={2.25} />
                Open Calendar to book an appointment
              </button>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="schedule-plan-item-list">
              {appts.map((a) => {
                const align = alignmentForApt(a.scheduled_start);
                const alignClass = !align ? "" :
                  align.kind === "in"    ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                  align.kind === "early" ? "bg-amber-50 text-amber-700 border-amber-200" :
                                           "bg-amber-50 text-amber-700 border-amber-200";
                return (
                <li key={a.id}>
                  <button
                    type="button"
                    onClick={() => pick(a.id)}
                    disabled={submitting}
                    data-testid={`schedule-plan-item-pick-${a.id}`}
                    className="w-full text-left px-3 py-3 hover:bg-slate-50 disabled:opacity-50 rounded-lg flex items-start gap-3"
                  >
                    <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center shrink-0">
                      <Calendar className="w-4 h-4 text-teal-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-slate-900">{formatRange(a.scheduled_start, a.scheduled_end)}</p>
                        {align ? (
                          <span className={`inline-flex items-center text-[10px] font-bold tracking-wider uppercase rounded px-1.5 py-0.5 border ${alignClass}`}>
                            {align.kind === "in" ? "✓ in window" : align.label}
                          </span>
                        ) : null}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Status: {a.status}
                        {a.notes ? <span className="ml-2 truncate">· {a.notes}</span> : null}
                      </p>
                    </div>
                  </button>
                </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
